import React, { useEffect, useState } from 'react';
import { getReportList, ReportListItem } from '../../components/services/ReportService';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { useLocation } from 'react-router-dom';
import Card from '../../components/Card';
import { DataTable, ColumnConfig } from '../../components/Table';

function getQueryParam(search: string, key: string) {
  const params = new URLSearchParams(search);
  return params.get(key);
}

const DownloadReport = () => {
  const { selectedTenantId } = useTenantSelection();
  const location = useLocation();
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState<string>(getQueryParam(location.search, 'reportType') || '');

  // Report types from ReportView
  const reportTypes = [
    { label: 'Executive Report', value: 'executivereport' },
    { label: 'Daily Update Summary', value: 'dailyupdatesummary' },
    { label: 'Installed Windows Updates', value: 'installedwindowsupdates' },
    { label: 'Missing Third-Party & Windows Updates', value: 'missingthirdpartyandwindowsupdates' },
    { label: 'Missing Windows Critical Updates', value: 'missingwindowscriticalupdates' },
    { label: 'Missing Windows Updates', value: 'missingwindowsupdates' },
    { label: 'Reboot Required', value: 'rebootrequired' },
    { label: 'Weekly Update Summary', value: 'weeklyupdatesummary' },
    { label: 'Severity Report', value: 'severity' },
  ];

  const tenantGroupId = getQueryParam(location.search, 'tenantGroupId') || null;
  const reportType = selectedReportType || null;

  const fetchReports = async () => {
    if (!selectedTenantId) return;
    setLoading(true);
    try {
      const res = await getReportList(selectedTenantId, tenantGroupId ?? '', reportType ?? '');
      if (res.isSuccess) setReports(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenantId, tenantGroupId, reportType]);

  // Update query param and refetch when dropdown changes
  const handleReportTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value;
    setSelectedReportType(newType);
    const params = new URLSearchParams(location.search);
    if (newType) {
      params.set('reportType', newType);
    } else {
      params.delete('reportType');
    }
    window.history.replaceState(null, '', `${location.pathname}?${params.toString()}`);
    // fetchReports will be triggered by useEffect
  };

  const downloadReport = (url: string, reportType: string) => {
    window.dispatchEvent(new CustomEvent('performDownload', { detail: { url, reportType } }));
  };

  const tableColumns: ColumnConfig[] = [
    {
      key: 'reportType',
      header: 'Report Type',
      width: '24%',
      align: 'left',
      cellStyle: { minWidth: 0 },
      render: (value) => (
        <div className="flex items-center gap-3 min-w-0">
          <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <span className="text-white font-medium truncate block">{value}</span>
        </div>
      ),
    },
    {
      key: 'tenantName',
      header: 'Tenant',
      width: '16%',
      align: 'left',
      cellStyle: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    },
    {
      key: 'metadata',
      header: 'Tenant Group',
      width: '10%',
      align: 'left',
      cellStyle: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
      render: (_, item) => <span className="block truncate">{item.metadata?.tenantGroupName || '-'}</span>,
    },
    {
      key: 'expiryTime',
      header: 'Expiry Time',
      width: '10%',
      align: 'left',
      render: (_, item) => (
        <div className="text-gray-400 text-sm">
          <div>
            {item.metadata?.expiryTime
              ? new Date(item.metadata.expiryTime).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })
              : '-'}
          </div>
          <div className="text-xs text-gray-500">
            {item.metadata?.expiryTime
              ? new Date(item.metadata.expiryTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'size',
      header: 'Size',
      width: '10%',
      align: 'left',
      render: (value) => <span className="text-gray-400 text-sm">{(value / 1024).toFixed(2)} KB</span>,
    },
    {
      key: 'relativeUrl',
      header: 'Action',
      width: '10%',
      align: 'center',
      render: (_, item) => (
        <button
          className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg transition-colors duration-200"
          onClick={() => downloadReport(item.relativeUrl, item.reportType)}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
          Download
        </button>
      ),
    },
  ];

  return (
    <div className="min-h-screen p-6">
      <div className="mx-auto">
        <Card className="mb-8 flex items-center justify-between">
          <div>
            {/* <h2 className="text-3xl font-bold text-white mb-2">Download Reports</h2> */}
            <p className="text-gray-400">Access and download your generated reports</p>
          </div>
          <div className="relative">
            <select
              id="reportTypeFilter"
              value={selectedReportType}
              onChange={handleReportTypeChange}
              className="appearance-none bg-[#2a2a2a] text-white pl-4 pr-10 py-2.5 rounded-lg border border-[#444] hover:border-red-500/50 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all duration-200 cursor-pointer min-w-[180px] shadow-lg [&>option]:bg-[#2a2a2a] [&>option]:text-white"
            >
              <option value="">All Types</option>
              {reportTypes.map((rt) => (
                <option key={rt.value} value={rt.value} className="bg-[#2a2a2a] text-white">
                  {rt.label}
                </option>
              ))}
            </select>
            {/* Custom dropdown arrow */}
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-400">Loading reports...</p>
            </div>
          </div>
        ) : reports.length === 0 ? (
          <Card className="rounded-xl p-12 text-center">
            <div className="text-gray-500 mb-4">
              <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-300 mb-2">No reports found</h3>
            <p className="text-gray-500">Try adjusting your filters or check back later</p>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <DataTable
              columns={tableColumns}
              data={reports}
              hoverEffect={true}
              getRowId={(item: ReportListItem) => item.blobName}
              containerStyle={{ background: 'transparent', borderRadius: 0, boxShadow: 'none' }}
              headerStyle={{
                backgroundColor: '#161617',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                padding: '0.75rem 1.5rem',
                borderBottom: '2px solid #2a2a2a',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                minHeight: 60,
              }}
              rowStyle={{
                fontSize: 15,
                padding: '1rem 1.5rem',
                borderBottom: '1px solid #3a3a3a',
                minHeight: 60,
              }}
            />
          </Card>
        )}
      </div>
    </div>
  );
};

export default DownloadReport;
